'use client';

import React, { forwardRef } from 'react';
import { cx } from '../../../utils/cx';

const SEMANTIC_NAME = 'RomaToolbarLayout';
const SEMANTIC_NAME_DIVIDER = 'RomaToolbarLayoutDivider';

const gapClasses = {
    none: 'gap-none [--hit-slop-x:0px]',
    xxs: 'gap-xxs [--hit-slop-x:calc(var(--spacing-xxs)/2)]',
    xs: 'gap-xs [--hit-slop-x:calc(var(--spacing-xs)/2)]',
    sm: 'gap-sm [--hit-slop-x:calc(var(--spacing-sm)/2)]',
    md: 'gap-md [--hit-slop-x:calc(var(--spacing-md)/2)]',
    lg: 'gap-lg [--hit-slop-x:calc(var(--spacing-lg)/2)]',
    xl: 'gap-xl [--hit-slop-x:calc(var(--spacing-xl)/2)]',
} as const;

export type ToolbarLayoutProps = React.HTMLAttributes<HTMLDivElement> & {
    /** Space between controls; also sets each utility button's horizontal hit area to half the gap. */
    gap?: keyof typeof gapClasses;
};
export type ToolbarLayoutDividerProps = React.HTMLAttributes<HTMLDivElement> & {
    type?: 'line' | 'spacer';
};

/**
 * Shares toolbar spacing and hit areas without owning surface, focus or positioning.
 */
export const ToolbarLayoutRoot = forwardRef<HTMLDivElement, ToolbarLayoutProps>(
    ({ children, className, gap = 'xs', role = 'toolbar', ...props }, ref) => (
        <div
            {...props}
            ref={ref}
            role={role}
            className={cx(
                SEMANTIC_NAME,
                'flex flex-row',
                gapClasses[gap],
                // override the default button styles within the toolbar to increase border radius
                '[&_.RomaButton]:rounded-sm [&_.RomaButton:before]:rounded-[7px]',
                className,
            )}
        >
            {children}
        </div>
    ),
);

/**
 * Separates tool groups with a line or empty space.
 */
export const ToolbarLayoutDivider = forwardRef<HTMLDivElement, ToolbarLayoutDividerProps>(
    ({ className, type = 'line', ...props }, ref) => (
        <div
            {...props}
            ref={ref}
            aria-hidden="true"
            className={cx(
                SEMANTIC_NAME_DIVIDER,
                'flex py-sm shrink-0 items-center justify-center self-stretch',
                type === 'line' ? 'w-lg px-0' : 'px-md',
                className,
            )}
        >
            {type === 'line' && <span className="h-full w-px bg-border-primary_alpha" />}
        </div>
    ),
);

ToolbarLayoutRoot.displayName = SEMANTIC_NAME;
ToolbarLayoutDivider.displayName = SEMANTIC_NAME_DIVIDER;

export const ToolbarLayout = Object.assign(ToolbarLayoutRoot, {
    Divider: ToolbarLayoutDivider,
});
